import React from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { blogPosts } from '../utils/mockData';
import legacyRedirects from '../seo/redirects.json';

import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';

// Inline Markdown-style link parser: converts "[text](url)" into real anchors.
// Internal paths (starting with "/") become <Link> for SPA navigation;
// anything else is rendered as an external <a> with safe rel attributes.
// Falls back to the raw string if no links are present, so existing posts
// render identically.
const INLINE_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const renderInline = (text) => {
  if (!text || !text.includes('[')) return text;
  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = INLINE_LINK_RE.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const [, label, href] = m;
    if (href.startsWith('/')) {
      parts.push(
        <Link
          key={`l-${m.index}`}
          to={href}
          className="text-teal-700 font-medium underline-offset-2 hover:underline"
        >
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a
          key={`a-${m.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal-700 font-medium underline-offset-2 hover:underline"
        >
          {label}
        </a>
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Look up by slug first (for SEO-friendly URLs), then fall back to numeric id.
  // Legacy /blog/:numeric URLs stay valid; new posts use /blog/:slug.
  const post =
    blogPosts.find((p) => p.slug === id) ||
    blogPosts.find((p) => p.id === parseInt(id));

  const postUrl = post ? `/blog/${post.slug || post.id}` : `/blog/${id}`;

  usePageSeo({
    title: post ? (post.seo?.title || post.title) : 'Blog Post | WPAA Insights',
    description: post?.seo?.description || post?.excerpt || 'Read expert insights on UAE audit, tax and advisory topics from the White Paper Accounts Auditing team.',
    path: postUrl,
    noindex: !post
  });

  const redirectTo = legacyRedirects[`/blog/${id}`] || (post?.slug && id !== post.slug ? `/blog/${post.slug}` : null);
  if (redirectTo) return <Navigate to={redirectTo} replace />;

  // If post not found, show error
  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-6">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <JsonLd
        id={`ld-article-${post.id}`}
        data={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: post.date,
          author: {
            '@type': 'Organization',
            name: post.author,
            url: 'https://wpaauae.com'
          },
          publisher: {
            '@type': 'Organization',
            name: 'White Paper Accounts Auditing',
            logo: {
              '@type': 'ImageObject',
              url: 'https://wpaauae.com/logo.png'
            }
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://wpaauae.com${postUrl}`
          },
          articleSection: post.category
        }}
      />
      {/* Back Button */}
      <section className="pt-24 pb-8 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center text-teal-600 hover:text-teal-700 font-medium mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </button>
        </div>
      </section>

      {/* Hero Image */}
      <section className="px-4 bg-gray-50 pb-12">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              loading="eager"
              fetchPriority="high"
              className="w-full max-h-[500px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          {/* Category Badge */}
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-200 mb-4">
            {post.category}
          </Badge>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
            <div className="flex items-center space-x-2">
              <User size={18} />
              <span className="font-medium">{post.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={18} />
              <span>{post.readTime}</span>
            </div>
          </div>

          {/* Article Excerpt */}
          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8 rounded-r-lg">
            <p className="text-lg text-gray-700 leading-relaxed italic">
              {post.excerpt}
            </p>
          </div>

          {/* Article Body */}
          {Array.isArray(post.content) && post.content.length > 0 ? (
            <div className="prose prose-lg max-w-none">
              {post.content.map((block, idx) => {
                if (block.type === 'intro') {
                  return (
                    <p key={idx} className="text-xl text-gray-800 leading-relaxed mb-6 font-medium">
                      {renderInline(block.text)}
                    </p>
                  );
                }
                if (block.type === 'paragraph') {
                  return (
                    <p key={idx} className="text-gray-700 leading-relaxed mb-6">
                      {renderInline(block.text)}
                    </p>
                  );
                }
                if (block.type === 'h2') {
                  return (
                    <h2 key={idx} className="text-3xl font-bold text-gray-900 mt-12 mb-4">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'h3') {
                  return (
                    <h3 key={idx} className="text-2xl font-bold text-gray-900 mt-8 mb-3">
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === 'subheading') {
                  return (
                    <p key={idx} className="text-gray-900 font-semibold mb-3 mt-6">
                      {renderInline(block.text)}
                    </p>
                  );
                }
                if (block.type === 'list') {
                  return (
                    <ul key={idx} className="list-disc list-outside ml-6 space-y-2 mb-6 text-gray-700">
                      {block.items.map((item, i) => (
                        <li key={i} className="leading-relaxed">{renderInline(item)}</li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === 'callout') {
                  return (
                    <div key={idx} className="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-r-lg">
                      {block.title && (
                        <p className="font-bold text-teal-900 mb-1">{block.title}</p>
                      )}
                      <p className="text-teal-900">{renderInline(block.text)}</p>
                    </div>
                  );
                }
                if (block.type === 'table') {
                  return (
                    <div key={idx} className="my-8 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                      <table className="w-full text-sm">
                        {Array.isArray(block.headers) && block.headers.length > 0 && (
                          <thead className="bg-teal-50 text-teal-900">
                            <tr>
                              {block.headers.map((h, i) => (
                                <th
                                  key={i}
                                  className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody className="divide-y divide-gray-100">
                          {(block.rows || []).map((row, ri) => (
                            <tr key={ri} className={ri % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}>
                              {row.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className={`px-4 py-3 text-gray-700 leading-relaxed align-top ${
                                    ci === 0 ? 'font-semibold text-gray-900 whitespace-nowrap' : ''
                                  }`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          ) : (
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-900 mt-8 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                {post.excerpt} In this comprehensive guide, we'll explore all the essential aspects you need to understand 
                about this important topic for businesses operating in the UAE.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Key Points to Consider</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Understanding the regulatory landscape in the UAE is crucial for business success. Our experts at White Paper 
                have compiled this guide based on years of experience working with diverse clients across various industries.
              </p>

              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Comprehensive understanding of UAE regulations and compliance requirements</li>
                <li>Strategic planning approaches tailored to your business needs</li>
                <li>Best practices for maintaining compliance while optimizing operations</li>
                <li>Common pitfalls to avoid and how to navigate challenges</li>
                <li>Expert insights from our experienced team of professionals</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Why This Matters for Your Business</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                In today's dynamic business environment, staying informed about the latest regulatory changes and best practices 
                is not just recommended—it's essential. Our team at White Paper works closely with businesses to ensure they 
                remain compliant while maximizing operational efficiency.
              </p>

              <div className="bg-gray-50 p-6 rounded-xl my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Tip</h3>
                <p className="text-gray-700">
                  "Regular consultation with professional advisors can help you stay ahead of regulatory changes and identify 
                  opportunities for optimization. Don't wait until issues arise—proactive planning is key to success."
                </p>
                <p className="text-sm text-gray-600 mt-2 italic">— White Paper Advisory Team</p>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Taking Action</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Armed with this knowledge, you're better positioned to make informed decisions for your business. However, 
                every business is unique, and generic advice may not address your specific circumstances. That's where 
                professional guidance becomes invaluable.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Conclusion</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Navigating the complexities of {post.category.toLowerCase()} in the UAE requires expertise, attention to detail, 
                and a proactive approach. At White Paper, we're committed to helping businesses like yours achieve compliance 
                and operational excellence.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                Whether you're a startup establishing your foundation or an established company seeking to optimize your operations, 
                our team of certified professionals is here to support your journey.
              </p>
            </div>
          )}

          {/* Call to Action */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 mt-12 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Need Expert Guidance?</h3>
            <p className="text-teal-100 text-lg mb-6 max-w-2xl mx-auto">
              Our team at White Paper is ready to help you navigate these complexities and achieve your business goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 px-8">
                  Schedule Consultation
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts
              .filter(p => p.id !== post.id && p.category === post.category)
              .slice(0, 3)
              .map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug || relatedPost.id}`}>
                  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all group cursor-pointer">
                    <div className="bg-gray-50 h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        loading="lazy"
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                      />
                    </div>
                    <div className="p-6">
                      <Badge className="bg-teal-100 text-teal-700 mb-3">{relatedPost.category}</Badge>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        {relatedPost.title}
                      </h3>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>{relatedPost.date}</span>
                        <span>{relatedPost.readTime}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
